import { useEffect, useState } from "react"
import { useQuery } from "urql"

import type { Company, SupplierListQuery, SupplierListQueryVariables } from "@/graphql/codegen/graphql"
import { SupplierListDocument } from "@/graphql/codegen/graphql"

interface UseGetSuppliersReturn {
  suppliers: Company[]
  isFetching: boolean
  loadMore: () => void
}

const SUPPLIER_COMPANY_TYPE = "COMMODITY_VENDOR"
const DEFAULT_LIMIT = 20
const GET_ALL_LIMIT = 300

interface UseGetSuppliersProps {
  query?: string
  companyIds?: string[]
  getAllSuppliers?: boolean
}

export const useGetSuppliers = ({
  query,
  companyIds,
  getAllSuppliers = false,
}: UseGetSuppliersProps): UseGetSuppliersReturn => {
  const [offset, setOffset] = useState(0)
  const [supplierList, setSupplierList] = useState<Company[]>([])
  const [hasNextPage, setHasNextPage] = useState(true)

  const limit = getAllSuppliers ? GET_ALL_LIMIT : DEFAULT_LIMIT

  const [{ data, fetching }] = useQuery<SupplierListQuery, SupplierListQueryVariables>({
    query: SupplierListDocument,
    variables: {
      input: {
        filter: {
          types: [SUPPLIER_COMPANY_TYPE],
          ...(query && query?.length > 1 && { query }),
          ...(companyIds && companyIds?.length && { companyIds }),
        },
        limit,
        offset,
      },
    },
  })

  useEffect(() => {
    setOffset(0)
    setSupplierList([])
    setHasNextPage(true)
  }, [query])

  useEffect(() => {
    if (!data) return

    const companies =
      data?.company?.list?.__typename === "CompanyListSuccess" ? (data?.company?.list?.companies as Company[]) : []

    setSupplierList((prev) => {
      const uniqueIds = new Set(prev.map((company) => company.companyId))
      return offset === 0 ? companies : [...prev, ...companies.filter((company) => !uniqueIds.has(company.companyId))]
    })

    if (getAllSuppliers) {
      if (companies.length < limit) {
        setHasNextPage(false)
      } else if (hasNextPage) {
        loadMore()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const loadMore = () => {
    if (getAllSuppliers && !hasNextPage) return
    setOffset((prevOffset) => prevOffset + limit)
  }

  return {
    suppliers: supplierList,
    isFetching: fetching,
    loadMore,
  }
}
